//
// checkpointer.ts
//
// Copyright 2024-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

// github.com/couchbase/couchbase-lite-core/blob/master/modules/docs/pages/replication-protocol.adoc

import type { CollectionID, Replicator } from "./replicator";
import { Checkpoint, type LocalSequence, type RemoteSequence, type RevID } from "./types";
import type * as blip from "@/blip/blip";
import type { JSONObject } from "@/util/json_types";
import { assert } from "@/util/assert";
import type * as logtape from "@logtape/logtape";
import type { WorkerParams } from "./endpoint";


const kSaveDelay = 2000;


/** Common configuration parameters for Pusher and Puller.  @internal */
export interface CheckpointerConfig {
    clientID            : string,     // Unique checkpoint ID for saving on server
    initialCheckpoint?  : Checkpoint, // Saved checkpoint from last replication, if any
    reset?              : boolean,    // If true, ignore saved checkpoints
}

/** @internal */
export interface CheckpointerDelegate {
    saveCheckpoint: (collection: CollectionID, clientID: string, checkpoint: Checkpoint) => Promise<void>,   // Saves updated checkpoint
}


/** Manages a collection's replication checkpoint for both its pusher and puller.  @internal */
export class Checkpointer {

    constructor(params: WorkerParams,
                private readonly config: CheckpointerConfig,
                private readonly delegate: CheckpointerDelegate,
                remoteCheckpoint: JSONObject)
    {
        this.replicator = params.replicator;
        this.collectionID = params.collectionID;
        this.collectionIndex = params.collectionIndex;
        this.#socket = params.socket;
        this.#logger = this.replicator.logger.getChild(["chkpt", this.collectionID]);
        this.#checkpoint = config.initialCheckpoint ?? new Checkpoint();
        const revID = this.#checkpointRevID = (remoteCheckpoint._rev ?? "") as RevID;
        const remote = Checkpoint.fromObject(remoteCheckpoint);

        this.#logger.debug `Checkpoint ID is ${config.clientID}. Current server revid is ${revID}.`;
        if (config.reset) {
            this.#logger.info `Resetting checkpoint by request. Replication will be slower!`;
            this.#checkpoint = new Checkpoint();
        } else if (this.#checkpoint.empty) {
            if (revID)
                this.#logger.warn `Checkpoint mismatch: No local checkpoint but server has ${remote}. Resetting.`;
            else
                this.#logger.info `Neither local nor server checkpoint`;
        } else if (remote && this.#checkpoint.equals(remote)) {
            this.#logger.info `Checkpoints match: ${remote}`;
        } else {
            this.#logger.warn `Checkpoint mismatch: I have ${this.#checkpoint}, server has ${remote}. Resetting.`;
            this.#checkpoint = new Checkpoint();
        }
    }

    stop() {
        this.stopTimer();
        this.#socket = undefined;
        this.#needsSave = false;
        this.#saveOperation = undefined;
    }

    get idle() {return !this.#timer && !this.#saveOperation;}

    get localSequence()  : LocalSequence | undefined {return this.#checkpoint.local;}
    get remoteSequence() : RemoteSequence | undefined {return this.#checkpoint.remote;}

    set localSequence(s: LocalSequence | undefined) {
        if (s !== this.#checkpoint.local) {
            assert(this.#socket !== undefined, "Can't set Checkpointer.localSequence after closing");
            this.#checkpoint.local = s;
            this.saveSoon();
        }
    }

    set remoteSequence(s: RemoteSequence | undefined) {
        if (!this.#socket)
            return;    // (ignore this call if it's made after stop() is called.)
        if (s !== this.#checkpoint.remote) {
            this.#checkpoint.remote = s;
            this.saveSoon();
        }
    }

    /** If there are unsaved changes, begins saving them immediately. */
    saveASAP() {
        if (this.#needsSave && !this.#saveOperation)
            void this.saveNow();
    }

    /** Mark that I have unsaved changes, and schedule a save after kSaveDelay. */
    private saveSoon() {
        this.#needsSave = true;
        if (this.#timer === undefined) {
            const timerFired = () => {
                this.#timer = undefined;
                void this.saveNow();
            };
            this.#timer = setTimeout(timerFired, kSaveDelay);
        }
    }

    private stopTimer() {
        if (this.#timer) {
            clearTimeout(this.#timer);
            this.#timer = undefined;
        }
    }

    async saveNow() : Promise<void> {
        // Sanity check:
        if (!this.#socket || !this.#needsSave)
            return;

        this.#logger.debug `saveNow (${this.#checkpoint})`;
        this.stopTimer();

        // If a previous save operation is still running, wait for it to finish:
        if (this.#saveOperation)
            await this.#saveOperation;

        this.#needsSave = false;
        const checkpointToSave = JSON.stringify(this.#checkpoint);
        const props: blip.Properties = {
            Profile:    "setCheckpoint",
            collection: this.collectionIndex,
            client:     this.config.clientID
        };
        if (this.#checkpointRevID)
            props.rev = this.#checkpointRevID;

        this.#logger.debug `sending setCheckpoint ${checkpointToSave} rev ${props.rev} ...`;
        this.#saveOperation = this.#socket.send(props, checkpointToSave, "nothrow");
        const reply = await this.#saveOperation;
        this.#saveOperation = undefined;
        if (reply.error) {
            this.#logger.error `Error saving checkpoint ${checkpointToSave} rev ${props.rev} to server: ${reply.error.toString()}`;
            //TODO: Recover from this
            return;
        }

        this.#checkpointRevID = reply.properties.rev as RevID;
        this.#logger.debug `Saved checkpoint ${checkpointToSave} to server as rev ${this.#checkpointRevID} ...`;

        // Now that the checkpoint is saved remotely, tell the delegate to save it locally:
        await this.delegate.saveCheckpoint(this.collectionID, this.config.clientID,
                                           this.#checkpoint);
        this.#logger.info `Saved checkpoint ${checkpointToSave}`;
        this.replicator.statusChanged_();
    }

    toString(): string { return `Checkpointer[${this.collectionID}]`; }

    readonly collectionID: CollectionID;
    readonly collectionIndex: number;
    private readonly replicator: Replicator;

    #logger             : logtape.Logger;
    #socket?            : blip.Socket;          // The BLIP connection
    #checkpoint         : Checkpoint;           // Current checkpoint object
    #checkpointRevID?   : RevID;                // Server-side revid of checkpoint
    #timer?             : number | NodeJS.Timeout; // Timer ID from `setTimeout`; for saving
    #saveOperation?     : Promise<blip.Message>;// Outgoing `setCheckpoint` request
    #needsSave          = false;                // True when state needs saving
}
